import { useState, useCallback, useEffect } from 'react';
import { TIME_GENERATION, DEFAULT_VALUES } from '../constants/slotConstants';

// Time utility functions
const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(':')) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const getOrderedWeekDays = () => {
  const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayIdx = new Date().getDay();
  return WEEK_DAYS.slice(todayIdx).concat(WEEK_DAYS.slice(0, todayIdx));
};

export const useSlotManagement = ({ backendSlots }) => {
  const [days, setDays] = useState(() => getOrderedWeekDays());
  const [slotsByDay, setSlotsByDay] = useState({});
  const [startTimeByDay, setStartTimeByDay] = useState({});
  const [endTimeByDay, setEndTimeByDay] = useState({});
  const [slotDurationByDay, setSlotDurationByDay] = useState({});
  const [dayEnabledByDay, setDayEnabledByDay] = useState({});

  const generateSlotsWithParams = useCallback((day, start, end, duration, existingSlots) => {
    const def = backendSlots.find((s) => s.id === day);
    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    // Guard against duration <= 0 to prevent infinite loop
    const effectiveDuration = duration > 0 ? duration : DEFAULT_VALUES.SLOT_DURATION;
    const slots = [];
    let cur = startMin;
    const maxIterations = TIME_GENERATION.MAX_ITERATIONS;
    let it = 0;
    while (cur < endMin && it < maxIterations) {
      const remaining = endMin - cur;
      const slotDuration = Math.min(effectiveDuration, remaining);
      slots.push({ time: minutesToTime(cur), duration: slotDuration });
      cur += effectiveDuration;
      it++;
    }

    const existingByTime = existingSlots.reduce((acc, slot) => {
      acc[slot.time] = slot;
      return acc;
    }, {});

    return slots.map(({ time: t, duration: slotDur }) => {
      const existingSlot = existingByTime[t];
      const fromDefCapacity = def?.defaultMaxCapacity ?? DEFAULT_VALUES.SLOT_CAPACITY;
      return {
        id: existingSlot?.id ?? `${day}-${t}`,
        time: t,
        capacity: existingSlot?.capacity ?? fromDefCapacity,
        active: existingSlot?.active ?? false,
        duration: slotDur
      };
    });
  }, [backendSlots]);

  const initializeSlotsData = useCallback((loadedSlots) => {
    const initialDays = getOrderedWeekDays();
    const newSlotsByDay = {};
    const newStartTimeByDay = {};
    const newEndTimeByDay = {};
    const newSlotDurationByDay = {};
    const newDayEnabledByDay = {};

    for (const day of initialDays) {
      const def = loadedSlots.find(s => s.id === day);
      // Use || (not ??) so that 0 and empty string also fall back to defaults
      const duration = def?.duration || DEFAULT_VALUES.SLOT_DURATION;
      const startTime = def?.startTime || DEFAULT_VALUES.START_TIME;
      const endTime = def?.endTime || DEFAULT_VALUES.END_TIME;

      newStartTimeByDay[day] = startTime;
      newEndTimeByDay[day] = endTime;
      newSlotDurationByDay[day] = duration;

      if (def?.dayEnabled !== undefined) {
        newDayEnabledByDay[day] = def.dayEnabled;
      } else {
        // If no explicit dayEnabled from backend, derive from slot active states:
        // day is enabled only if at least one slot is active.
        const hasActiveSlot = def?.slots?.some(s => s.active) ?? false;
        newDayEnabledByDay[day] = hasActiveSlot;
      }

      {
        const existingSlots = (def && def.slots && def.slots.length > 0)
          ? def.slots.map(s => ({ ...s, id: s.id ?? `${day}-${s.time}` }))
          : [];
        newSlotsByDay[day] = generateSlotsWithParams(day, startTime, endTime, duration, existingSlots);
      }
    }

    setDays(initialDays);
    setStartTimeByDay(newStartTimeByDay);
    setEndTimeByDay(newEndTimeByDay);
    setSlotDurationByDay(newSlotDurationByDay);
    setDayEnabledByDay(newDayEnabledByDay);
    setSlotsByDay(newSlotsByDay);
  }, [generateSlotsWithParams]);

  // Always run initializeSlotsData — even if backendSlots is empty, we need to reset
  // state to defaults rather than leaving stale data from a previous load.
  useEffect(() => {
    initializeSlotsData(backendSlots);
  }, [backendSlots, initializeSlotsData]);

  // Toggling a single slot only affects that slot's active state.
  // It does NOT affect dayEnabledByDay (ticket 3982).
  const updateSlot = useCallback((day, slotId, patch) => {
    setSlotsByDay(prev => ({
      ...prev,
      [day]: (prev[day] ?? []).map(s => (s.id === slotId ? { ...s, ...patch } : s))
    }));
  }, []);

  const setDayEnabled = useCallback((day, enabled) => {
    setDayEnabledByDay(prev => ({
      ...prev,
      [day]: enabled
    }));
  }, []);

  // "Enable Day" sets the day flag AND mass-toggles all slots for that day.
  // The inverse (a single slot toggled) does NOT affect the day flag.
  const toggleAllSlotsForDay = useCallback((day, enabled) => {
    setDayEnabledByDay(prev => ({
      ...prev,
      [day]: enabled
    }));
    setSlotsByDay(prev => {
      const currentSlots = prev[day] ?? [];
      if (currentSlots.length === 0) return prev;
      return {
        ...prev,
        [day]: currentSlots.map(slot => ({ ...slot, active: enabled }))
      };
    });
  }, []);

  // "All Days" sets the day flag AND mass-toggles all slots for every day.
  const toggleAllSlotsForAllDays = useCallback((enabled) => {
    setDayEnabledByDay(prev => {
      const next = { ...prev };
      for (const day of days) {
        next[day] = enabled;
      }
      return next;
    });
    setSlotsByDay(prev => {
      const next = { ...prev };
      for (const day of days) {
        next[day] = (next[day] ?? []).map(s => ({ ...s, active: enabled }));
      }
      return next;
    });
  }, [days]);

  const updateDayTimeConfiguration = useCallback((day, newConfig) => {
    const { start, end, duration } = newConfig;

    if (duration) {
      setSlotDurationByDay(prev => ({ ...prev, [day]: duration }));
    }
    if (start) {
      setStartTimeByDay(prev => ({ ...prev, [day]: start }));
    }
    if (end) {
      setEndTimeByDay(prev => ({ ...prev, [day]: end }));
    }

    setSlotsByDay(prev => {
      const currentSlots = prev[day] ?? [];
      const newStartTime = start ?? startTimeByDay[day] ?? DEFAULT_VALUES.START_TIME;
      const newEndTime = end ?? endTimeByDay[day] ?? DEFAULT_VALUES.END_TIME;
      const newDuration = duration ?? slotDurationByDay[day] ?? DEFAULT_VALUES.SLOT_DURATION;

      return {
        ...prev,
        [day]: generateSlotsWithParams(day, newStartTime, newEndTime, newDuration, currentSlots)
      };
    });
  }, [startTimeByDay, endTimeByDay, slotDurationByDay, generateSlotsWithParams]);

  const updateAllDaysTimeConfiguration = useCallback((newConfig) => {
    const { start, end, duration } = newConfig;

    const newStartTimeByDay = { ...startTimeByDay };
    const newEndTimeByDay = { ...endTimeByDay };
    const newSlotDurationByDay = { ...slotDurationByDay };

    for (const day of days) {
      if (start) newStartTimeByDay[day] = start;
      if (end) newEndTimeByDay[day] = end;
      if (duration) newSlotDurationByDay[day] = duration;
    }

    setStartTimeByDay(newStartTimeByDay);
    setEndTimeByDay(newEndTimeByDay);
    setSlotDurationByDay(newSlotDurationByDay);

    setSlotsByDay(prev => {
      const next = { ...prev };
      for (const day of days) {
        const existing = prev[day] ?? [];
        next[day] = generateSlotsWithParams(
          day,
          newStartTimeByDay[day],
          newEndTimeByDay[day],
          newSlotDurationByDay[day],
          existing
        );
      }
      return next;
    });
  }, [days, startTimeByDay, endTimeByDay, slotDurationByDay, generateSlotsWithParams]);

  const updateCapacityForDay = useCallback((day, capacity) => {
    setSlotsByDay(prev => ({
      ...prev,
      [day]: (prev[day] ?? []).map(s => ({ ...s, capacity }))
    }));
  }, []);

  const updateCapacityForAllDays = useCallback((capacity) => {
    setSlotsByDay(prev => {
      const next = { ...prev };
      for (const day of days) {
        next[day] = (next[day] ?? []).map(s => ({ ...s, capacity }));
      }
      return next;
    });
  }, [days]);

  const updateDefaultCapacity = useCallback((dayToUpdate, capacity, applyToAll) => {
    setSlotsByDay(prev => {
      const next = { ...prev };
      const daysToUpdate = applyToAll ? Object.keys(next) : [dayToUpdate];
      for (const day of daysToUpdate) {
        if (next[day]) {
          next[day] = next[day].map(s => ({ ...s, capacity }));
        }
      }
      return next;
    });

    const updatedBackendConfig = days.map(day => {
      const shouldUpdate = applyToAll || day === dayToUpdate;
      const currentSlots = slotsByDay[day] || [];

      const updatedSlots = shouldUpdate
        ? currentSlots.map(s => ({ ...s, capacity }))
        : currentSlots;

      return {
        id: day,
        startTime: startTimeByDay[day] || DEFAULT_VALUES.START_TIME,
        endTime: endTimeByDay[day] || DEFAULT_VALUES.END_TIME,
        duration: slotDurationByDay[day] || DEFAULT_VALUES.SLOT_DURATION,
        defaultMaxCapacity: shouldUpdate ? capacity : (backendSlots.find(d => d.id === day)?.defaultMaxCapacity ?? capacity),
        slots: updatedSlots.map(({ id, time, active, capacity: slotCapacity }) => ({ id, time, active, capacity: slotCapacity, duration: slotDurationByDay[day] || DEFAULT_VALUES.SLOT_DURATION }))
      };
    });

    return updatedBackendConfig;
  }, [days, slotsByDay, startTimeByDay, endTimeByDay, slotDurationByDay, backendSlots]);

  return {
    days,
    slotsByDay,
    startTimeByDay,
    endTimeByDay,
    slotDurationByDay,
    dayEnabledByDay,
    setDays,
    updateSlot,
    setDayEnabled,
    toggleAllSlotsForDay,
    toggleAllSlotsForAllDays,
    updateDayTimeConfiguration,
    updateAllDaysTimeConfiguration,
    updateCapacityForDay,
    updateCapacityForAllDays,
    updateDefaultCapacity
  };
};

export { timeToMinutes, minutesToTime };
